import type { ProfileRowTier } from "./tier-dot";

export interface LadderUnavailableInput {
  /** Server capability (GET /api/verification/field-capabilities). */
  supportsBank: boolean;
  supportsDocument: boolean;
  /** The server's words for why not. Absent when the capability is true. */
  bankReason?: string;
  documentReason?: string;
  /**
   * The PostHog bank-income cohort gate. A capability the server says EXISTS
   * but that we have not switched on for this user yet — a different fact
   * from absence, and the whole reason this function exists.
   */
  bankComingSoon: boolean;
}

/**
 * Copy for a rollout gate. Deliberately NOT server-authored: the server's
 * capability table answers "can Sill ever do this", which for bank income is
 * yes. Whether it is switched on for this user is a client-side flag, so the
 * client owns this one sentence.
 */
const BANK_COMING_SOON = "Bank income verification is coming soon.";

/**
 * Builds TierLadder's `unavailable` map: which rungs this input has no path
 * to, and the reason to show for each.
 *
 * Exists because "we cannot do this" and "we have not turned this on yet" are
 * different facts that were previously collapsed into one boolean, and the
 * collapsed version told the buyer something false. Under the bank-income
 * rollout flag the income row rendered "This input can't be securely
 * verified — Plaid doesn't cover it", when Plaid covers it fine.
 *
 * Order matters: `bankComingSoon` is checked FIRST, because it only applies
 * to a row the server already says is bank-verifiable. A row that is both
 * unsupported and flagged should report the absence, not the rollout.
 *
 * A missing reason still disables the rung. The tooltip is the nice-to-have;
 * not offering a climb we cannot honour is the requirement, and the capability
 * table's own tests pin that an unsupported field always carries a reason.
 */
export function ladderUnavailable({
  supportsBank,
  supportsDocument,
  bankReason,
  documentReason,
  bankComingSoon,
}: LadderUnavailableInput): Partial<Record<ProfileRowTier, string>> {
  const unavailable: Partial<Record<ProfileRowTier, string>> = {};
  if (!supportsBank) {
    unavailable["bank-verified"] = bankReason ?? "";
  } else if (bankComingSoon) {
    unavailable["bank-verified"] = BANK_COMING_SOON;
  }
  if (!supportsDocument) {
    unavailable["document-backed"] = documentReason ?? "";
  }
  return unavailable;
}
